// assets/schema-cleanup.js
(() => {
  'use strict';

  // ===== Feature flag / guard =====
  if (window.__SCHEMA_CLEANUP_V3__ === false) return;
  if (typeof window.__SCHEMA_CLEANUP_V3__ === 'undefined') {
    window.__SCHEMA_CLEANUP_V3__ = true;
  }

  // Only run on City Index pages (adjust condition if needed):
  // Example: page URL contains "/pages/flower-delivery-by-city" OR template "page"
  try {
    const isCityIndex =
      location.pathname.indexOf('/pages/flower-delivery-by-city') > -1 ||
      document.documentElement.hasAttribute('data-city-index');

    if (!isCityIndex) return;
  } catch (e) {
    // If any error determining page, fail closed (do nothing)
    return;
  }

  // Don’t run inside Theme Editor to avoid interfering with previews
  if (window.Shopify && window.Shopify.designMode) return;

  // ===== Utilities =====
  const now = () => (performance && performance.now ? performance.now() : Date.now());

  function debounce(fn, delay) {
    let t = null;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), delay);
    };
  }

  // ===== Config =====
  const IDLE_MS = 5000;         // stop observing after 5s of no mutations
  const RUN_MAX = 2;            // run cleanup at most twice (initial + one follow-up)
  const RUN_COOLDOWN_MS = 3000; // minimum gap between cleanups
  const SCHEMA_SELECTOR = 'script[type="application/ld+json"]';

  // Whitelist logic:
  // 1) Any script tag with data-universal-schema="true" stays
  // 2) First occurrence per @type group stays; duplicates removed
  const isWhitelisted = (scriptEl) => {
    return scriptEl.hasAttribute('data-universal-schema') ||
           scriptEl.getAttribute('data-universal-schema') === 'true';
  };

  let lastRun = 0;
  let runs = 0;
  let observer = null;
  let lastMutationAt = now();

  function parseType(json) {
    try {
      const data = JSON.parse(json);
      // Handle arrays or single objects
      if (Array.isArray(data)) {
        // Return a stable key based on array of @type
        const types = data.map(item => (item && item['@type']) || 'Unknown').join('|');
        return types || 'UnknownArray';
      }
      return data && data['@type'] ? data['@type'] : 'Unknown';
    } catch {
      return 'Unparsable';
    }
  }

  function cleanupSchemas() {
    const elapsed = now() - lastRun;
    if (elapsed < RUN_COOLDOWN_MS) return;
    if (runs >= RUN_MAX) return;

    lastRun = now();
    runs += 1;

    const scripts = Array.from(document.querySelectorAll(SCHEMA_SELECTOR));
    const seen = new Map(); // type -> firstElement

    scripts.forEach((el) => {
      try {
        if (isWhitelisted(el)) {
          // keep whitelisted
          return;
        }
        const raw = el.textContent || '';
        const key = parseType(raw);
        if (!seen.has(key)) {
          seen.set(key, el); // keep first
          return;
        }
        // Duplicate of this @type => remove
        el.parentNode && el.parentNode.removeChild(el);
      } catch {
        // If parsing or removal fails, fail soft
      }
    });
  }

  const debouncedCleanup = debounce(cleanupSchemas, 250);

  function startObserving() {
    if (observer) return;

    observer = new MutationObserver(() => {
      lastMutationAt = now();
      debouncedCleanup();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true
    });

    // Initial pass once DOM is ready
    cleanupSchemas();

    // Periodic check to disconnect after idle
    const idleTimer = setInterval(() => {
      const since = now() - lastMutationAt;
      if (since >= IDLE_MS) {
        try {
          observer && observer.disconnect();
        } catch {}
        observer = null;
        clearInterval(idleTimer);
        // One final pass to ensure stability
        cleanupSchemas();
      }
    }, 1000);
  }

  // Prefer requestIdleCallback to avoid main thread contention; fallback to DOMContentLoaded
  const boot = () => startObserving();

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(boot, { timeout: 2000 });
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Expose a tiny debug hook (optional)
  window.__SchemaCleanupV3 = {
    run: cleanupSchemas,
    status: () => ({ runs, observerActive: !!observer })
  };
})();

